// janus-engine/statuses/gms-tools.ts — Lane GMS (2026-09-27): the pure resolvers for the GM status tools.
//
// Each resolver is a legacy endpoint (options/tools/triggers/*, options/tools/statuses/*) with its rules COPIED, run
// against the row the browser already holds. It answers one of three outcomes, the A1 boundary shape
// (janus-engine/actions/self-status.ts):
//   supported   — {before, after}: options/game/actions/status_truth_commit_inc.php CASes `before`, persists `after`
//                 through the SAME legacy writer, and patches the caches from the compile's own projection.
//   refused     — the legacy endpoint would refuse with this exact message; nothing is sent.
//   unsupported — outside this lane's proven boundary: the caller runs the legacy endpoint UNTOUCHED.
//
// S1 — trigger toggle / clear (toggle_trigger_active_inc.php, clear_triggers_for_caster_inc.php). The row's `triggers`
// bucket is enrich_creature_arr_with_triggers(): newest row per ability, identity/audit columns stripped,
// trigger_inactive / trigger_target_ids elided at their defaults. Toggle = the newest row updated in place and made
// GLOBAL (target ids survive only when it already was), else a new row with a browser-minted id; clear = every row gone.

import { ecId, ecLean } from '../ability/context.js';
import { readCreatureStatuses } from '../compile/creature-status-reader.js';
import { statusRows } from './expiry.js';
import { QUANTITY_FAMILIES } from '../ability/statuses/apply-status-effect.js';
import type { CampaignClockCols } from '../../shared/campaign-clock.js';
import { STATUS_TIME_UNITS as UNITS, statusIsPaused, statusTimeValueNonZero as nonZero } from './status-clock.js';
export { statusIsPaused } from './status-clock.js';

export const STATUS_TRUTH_SCHEMA = 'janus.status.truth.v1' as const;
export type StatusTruthKind = 'trigger_toggle' | 'trigger_clear' | 'status_hide' | 'status_reveal' | 'status_pause' | 'status_resume' | 'status_add' | 'status_delete' | 'creature_kill' | 'creature_resurrect' | 'creature_destroy' | 'creature_flee' | 'status_stack' | 'crafting_work' | 'crafting_stop' | 'crafting_start';

type Row = Record<string, unknown>;
const asRow = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(scalar(value) ?? '').trim().toUpperCase();
const uuidRe = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const present = (value: unknown): boolean => { const v = scalar(value); return v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0); };

export type GmsResolution<B, A> =
  /** `rebuild` (N6e, S5 / S6): the change runs a removal tail the page does not model — no C1 claim; the commit's legacy fan-out
   *  rebuilds every row it touched (the optimistic draw of the row itself still holds). */
  | { supported: true; before: B; after: A; rebuild?: string }
  | { supported: false; refused: true; message: string }
  | { supported: false; refused: false; reason: string };

/** The row's lean trigger rows (row.triggers), newest-per-ability as the compile attached them. */
export function triggerRows(row: Row): Row[] {
  return Array.isArray(row.triggers) ? (row.triggers as unknown[]).map(asRow).filter((r): r is Row => r !== null) : [];
}
/** ability_trigger_is_active() / abilityTriggerIsActive twin: the first row for the ability decides; absent = active. */
export function triggerRowFor(row: Row, abilityId: string): Row | null {
  const want = upper(abilityId);
  return triggerRows(row).find(r => upper(r.trigger_ability_id) === want) ?? null;
}

export interface TriggerToggleBefore { trigger_id: string | null; trigger_inactive: 0 | 1 }
export interface TriggerToggleAfter { trigger_id: string; trigger_inactive: 0 | 1; triggers: Row[] }
/**
 * Toggle (or explicitly set) the auto-fire flag of ONE ability on the actor (toggle_trigger_active_inc.php). `inactive`
 * is the value the badge asks for (1 = auto-fire OFF). The predicted `triggers` list puts the written row FIRST (it is
 * now the newest) — the committed frame carries the server's read-back list, which wins.
 */
export function resolveTriggerToggle(row: Row, input: { actorId: string; abilityId: string; inactive: unknown; uuid: () => string }): GmsResolution<TriggerToggleBefore, TriggerToggleAfter> {
  const abilityId = upper(input.abilityId);
  const actorId = upper(input.actorId);
  if (!uuidRe.test(abilityId)) return { supported: false, refused: true, message: 'Toggle Trigger: missing or invalid ability_id.' };
  if (!uuidRe.test(actorId)) return { supported: false, refused: true, message: 'Toggle Trigger: missing or invalid caster_id.' };
  const inactive: 0 | 1 = Number(scalar(input.inactive) ?? 0) !== 0 ? 1 : 0; // legacy clamp: anything non-zero is 1
  const current = triggerRowFor(row, abilityId);
  const currentId = current ? upper(current.trigger_id) : null;
  if (current && !uuidRe.test(currentId ?? '')) return { supported: false, refused: false, reason: 'trigger-id' };
  const before: TriggerToggleBefore = { trigger_id: currentId, trigger_inactive: current && Number(scalar(current.trigger_inactive) ?? 0) === 1 ? 1 : 0 };
  const id = currentId ?? upper(input.uuid());
  const wasGlobal = !current || !present(current.trigger_encounter_id);
  const written: Row = {
    trigger_id: id,
    trigger_encounter_id: null,
    trigger_caster_id: current ? upper(current.trigger_caster_id) || actorId : actorId,
  };
  if (current && wasGlobal && present(current.trigger_target_ids)) written.trigger_target_ids = current.trigger_target_ids;
  written.trigger_ability_id = current ? upper(current.trigger_ability_id) || abilityId : abilityId;
  if (inactive === 1) written.trigger_inactive = 1;
  const triggers = [written, ...triggerRows(row).filter(r => upper(r.trigger_ability_id) !== abilityId)];
  return { supported: true, before, after: { trigger_id: id, trigger_inactive: inactive, triggers } };
}

export interface TriggerClearBefore { trigger_ids: string[] }
/** Clear (soft-delete) every trigger row of the actor (clear_triggers_for_caster_inc.php): back to default-on everywhere. */
export function resolveTriggerClear(row: Row, input: { actorId: string }): GmsResolution<TriggerClearBefore, { triggers: Row[] }> {
  if (!uuidRe.test(upper(input.actorId))) return { supported: false, refused: true, message: 'Clear Triggers: missing or invalid caster_id.' };
  const ids = triggerRows(row).map(r => upper(r.trigger_id));
  if (ids.some(id => !uuidRe.test(id))) return { supported: false, refused: false, reason: 'trigger-id' };
  return { supported: true, before: { trigger_ids: [...ids].sort() }, after: { triggers: [] } };
}

/** The browser twin of the `triggers` cache op (incremental_recompile.php ft_inc_action_apply_ops): empty = no key. */
export function applyTriggerRows(row: Row, triggers: unknown): void {
  const rows = Array.isArray(triggers) ? (triggers as unknown[]).map(asRow).filter((r): r is Row => r !== null) : [];
  if (rows.length) row.triggers = rows.map(r => ({ ...r }));
  else delete row.triggers;
}

// ---------------------------------------------------------------------------------------------------------------------
// S3 — pause / resume: native clock planning; PHP CASes the held raw times and saves the planned columns.
export const STATUS_TIME_FIELDS = [
  'status_paused_campaign_year', 'status_paused_campaign_month', 'status_paused_campaign_day', 'status_paused_campaign_hour',
  'status_paused_campaign_minute', 'status_paused_campaign_second', 'status_ends_campaign_year', 'status_ends_campaign_month',
  'status_ends_campaign_day', 'status_ends_campaign_hour', 'status_ends_campaign_minute', 'status_ends_campaign_second',
] as const;
export type StatusTimeFields = Partial<Record<(typeof STATUS_TIME_FIELDS)[number], number | null>>;

/** The row's TARGET-side entry for a status id. */
export function statusEntry(row: Row, statusId: string): Row | null {
  const want = upper(statusId);
  return Object.values(readCreatureStatuses(row)).map(asRow).find(e => e !== null && upper(e.status_id) === want) ?? null;
}
/** is_status_effect_permanent() twin: no non-zero end component. */
export const statusIsPermanent = (entry: Row): boolean => !UNITS.some(u => nonZero(entry['status_ends_campaign_' + u]));
/** Snapshot of an entry's pause / end columns (absent = null). */
export function statusTimeFields(row: Row, statusId: string): StatusTimeFields | null {
  const entry = statusEntry(row, statusId);
  if (!entry) return null;
  const out: StatusTimeFields = {};
  for (const f of STATUS_TIME_FIELDS) out[f] = nonZero(entry[f]) ? Math.trunc(Number(scalar(entry[f]))) : null;
  return out;
}
/** The browser twin of the `status_time_fields` cache op (incremental_recompile.php): lean set / unset of the pause / end
 *  columns on EXISTING entries (target + caster lists), the rewritten row moved first (newest-updated order). */
export function applyStatusTimeFields(row: Row, statusId: string, fields: unknown): void {
  const f = asRow(fields);
  if (!f) return;
  const want = upper(statusId);
  const statuses = asRow(row.statuses);
  if (!statuses) return;
  for (const mode of ['target', 'caster'] as const) {
    const list = statusRows(row, mode);
    const i = (list as unknown[]).findIndex(e => upper(asRow(e)?.status_id) === want);
    if (i < 0) continue;
    const entry = { ...(list[i] as Row) };
    for (const k of STATUS_TIME_FIELDS) {
      if (!(k in f)) continue;
      if (nonZero(f[k])) entry[k] = Math.trunc(Number(scalar(f[k]))); else delete entry[k];
    }
    const next = [...list as unknown[]];
    next.splice(i, 1);
    statuses[mode] = [entry, ...next];
  }
}

export interface StatusPauseInput {
  statusId: string; action: 'pause' | 'resume';
  /** Full definition {items, item_layer_types} for the status item (row catalog → object cache); null = unknown. */
  definition: (id: string) => Row | null;
  /** ftLayerValue twin. */
  layer: (row: Row, key: string) => unknown;
  /** Held campaign clock and calendar readers, supplied by the adapter before enqueue. */
  clock: CampaignClockCols | null;
  timeSeconds: ((clock: CampaignClockCols) => number) | null;
  timeAddSeconds: ((clock: CampaignClockCols, seconds: number) => CampaignClockCols) | null;
  /** Canonical activity ids; Rest sleep application belongs to the browser adapter. */
  restingId: string | null; craftingId: string | null;
}
export function resolveStatusPause(row: Row, input: StatusPauseInput): GmsResolution<{ paused: 0 | 1; status_item_id: string; time_fields: StatusTimeFields }, { status_fields: { status_id: string; fields: StatusTimeFields } }> {
  const statusId = upper(input.statusId);
  const entry = statusEntry(row, statusId);
  if (!entry) return { supported: false, refused: false, reason: 'status-missing' };
  const itemId = upper(entry.status_item_id ?? asRow(asRow(entry.status_arr)?.items)?.item_id);
  if (!uuidRe.test(itemId)) return { supported: false, refused: false, reason: 'status-item' };
  // N6e (2026-10-02, R-065): a crafting WORKER row is judged like any status (pause_status_effect_inc.php had no crafting case: a worker
  // row is permanent, so it is refused below with the legacy words) — no hand-back. `craftingId` stays in the input (unused here).
  const def = input.definition(itemId) ?? asRow(entry.status_arr);
  if (!def) return { supported: false, refused: false, reason: 'definition-missing' };
  const pause = input.action === 'pause';
  if (!present(input.layer(def, 'status_effect_pausable'))) return { supported: false, refused: true, message: pause ? 'This status effect cannot be paused.' : 'This status effect cannot be resumed (not pausable).' };
  const paused = statusIsPaused(entry);
  if (pause && statusIsPermanent(entry)) return { supported: false, refused: true, message: 'Cannot pause a permanent status effect (no timed end).' };
  if (pause && paused) return { supported: false, refused: true, message: 'This status effect is already paused.' };
  if (!pause && !paused) return { supported: false, refused: true, message: 'This status effect is not paused.' };
  if (!pause && statusIsPermanent(entry)) return { supported: false, refused: true, message: 'Cannot resume a permanent status effect.' };
  const before = { paused: paused ? 1 as const : 0 as const, status_item_id: itemId, time_fields: statusTimeFields(row, statusId)! };
  if (!pause && (!input.timeSeconds || !input.timeAddSeconds)) return { supported: false, refused: true, message: 'Campaign calendar is missing; cannot resume status effect.' };
  if (!input.clock) return { supported: false, refused: false, reason: 'clock-missing' };
  const fields: StatusTimeFields = {};
  if (pause) {
    for (const u of UNITS) fields[`status_paused_campaign_${u}`] = input.clock[u];
  } else {
    const clock = (prefix: 'ends' | 'paused'): CampaignClockCols => Object.fromEntries(UNITS.map(u => [u, before.time_fields[`status_${prefix}_campaign_${u}`] ?? 0])) as unknown as CampaignClockCols;
    const remaining = input.timeSeconds!(clock('ends')) - input.timeSeconds!(clock('paused'));
    if (remaining < 0) return { supported: false, refused: true, message: 'Invalid pause data: end time is before pause time.' };
    const end = input.timeAddSeconds!(input.clock, remaining);
    for (const u of UNITS) { fields[`status_ends_campaign_${u}`] = end[u]; fields[`status_paused_campaign_${u}`] = null; }
  }
  return { supported: true, before, after: { status_fields: { status_id: statusId, fields } } };
}

// ---------------------------------------------------------------------------------------------------------------------
// S4 — ADD a status: the native GM boundary owns duration, immunity, quantity reapplication and the saved delta.
export interface StatusAddInput {
  targetId: string; statusItemId: string; casterId: string;
  definition: (id: string) => Row | null;
  layer: (row: Row, key: string) => unknown;
  /** The engine-only crafting tracker's id (add_status_effect.php refuses it by hand). */
  craftingProgressId: string | null;
  uuid: () => string;
  /** A8 (R-065): the page resolves a persistent-AoE add itself (zone-add.ts) — the zone family is no longer handed back. */
  zone?: boolean;
}
/** has_status_effect twin over the resolved TARGET statuses: does the row hold this item? */
export function rowHoldsStatusItem(row: Row, statusItemId: string): boolean {
  const want = upper(statusItemId);
  return Object.values(readCreatureStatuses(row)).some(e => {
    const entry = asRow(e);
    if (!entry) return false;
    const arr = asRow(entry.status_arr);
    return upper(entry.status_item_id ?? asRow(arr?.items)?.item_id ?? arr?.item_id) === want;
  });
}
export function resolveStatusAdd(row: Row, input: StatusAddInput): GmsResolution<{ held: 0 }, { status_added: { provisional_id: string; entry: Row } }> {
  const itemId = upper(input.statusItemId);
  const targetId = upper(input.targetId);
  if (!uuidRe.test(itemId) || !uuidRe.test(targetId)) return { supported: false, refused: false, reason: 'ids' };
  if (input.craftingProgressId && itemId === upper(input.craftingProgressId)) {
    return { supported: false, refused: true, message: 'That status is applied by the crafting system itself and cannot be added by hand.' };
  }
  const def = input.definition(itemId);
  if (!def) return { supported: false, refused: false, reason: 'definition-missing' };
  const itemType = String(scalar(asRow(def.items)?.item_type ?? def.item_type) ?? '').toLowerCase();
  if (itemType === 'trap_ability') return { supported: false, refused: false, reason: 'trap' };
  if (!input.zone && present(input.layer(def, 'persistent_aoe')) && present(input.layer(def, 'aoe_type'))) return { supported: false, refused: false, reason: 'persistent-aoe' };
  if (!(itemId in QUANTITY_FAMILIES) && rowHoldsStatusItem(row, itemId)) return { supported: false, refused: false, reason: 'status-already-held' };
  const provisional = upper(input.uuid());
  const casterId = uuidRe.test(upper(input.casterId)) ? upper(input.casterId) : targetId;
  const entry: Row = { status_id: provisional, status_target_id: targetId, status_stack: 1, status_item_id: itemId, ...(casterId !== targetId ? { status_caster_id: casterId } : {}) };
  return { supported: true, before: { held: 0 }, after: { status_added: { provisional_id: provisional, entry } } };
}
/** The browser twin of an added row landing (or a provisional one leaving): drop the provisional id, upsert the saved entry
 *  (target list; the caster list too when the row is self-cast — the A1 projection). null = remove only. */
export function applyStatusAdded(row: Row, provisionalId: string, entry: Row | null, front = false): void {
  // N4e 2026-10-02: `front` (an ADD) — the new row leads: the compile lists statuses newest-touched first (fetch_statuses), as the
  // served claim and a rebuild both have it, and the next claim compiles from this list (its order is the breakdown lines' order). A
  // delete's correction re-installs a row that was never touched — it keeps the legacy append.
  const statuses = (row.statuses ??= {}) as Row;
  const pid = upper(provisionalId);
  // The saved entry is ABBREVIATED (identity + stack): when it names the SAME row the page holds, merge into it — never drop and
  // re-add, which would strip the row's duration / pause / turn-expiry fields (Codex S9 review F7).
  const keep = entry !== null && upper(entry.status_id) === pid;
  for (const mode of ['target', 'caster'] as const) {
    const list = statusRows(row, mode).filter(e => keep || upper(e.status_id) !== pid);
    if (entry && (mode === 'target' || !entry.status_caster_id)) {
      const sid = upper(entry.status_id);
      const i = list.findIndex(e => upper(asRow(e)?.status_id) === sid);
      if (i >= 0) list[i] = { ...(list[i] as Row), ...entry }; else if (front) list.unshift({ ...entry }); else list.push({ ...entry });
    }
    statuses[mode] = list;
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// S5 / S6 — DELETE a status row (delete_creature_status_effect_inc.php single / …_all_connections_inc.php all). The browser
// pre-checks the boundary it can see; the server judges the rest on its own rows (connection group, zone anchor, the
// definition's charge / destroy tails) and answers 422 `unsupported:<why>` — the adapter then runs the legacy endpoint.
/** Twin of c1_live.php ft_c1_status_removal_tail_ids() (Hasted, Riding, Piloting, Rotting, Spoiling, Crafting) — the server
 *  list stays the authority; this only saves a round trip. */
export const STATUS_REMOVAL_TAIL_IDS: ReadonlySet<string> = new Set(['091B2BEE-7F61-D7DB-0CF1-6D255F4B7663', 'FF8D219A-2A83-0EE5-A222-E748753E8D0B',
  'A1F1FAF2-DA1E-D537-36C2-2B74DC82CB12', '62E83DEB-4569-AE3A-F671-FF514C508FD2', 'F7A1C3B2-5D64-4E18-9A70-2C8B41D5E093', '62ED7394-9C33-D41A-C426-1BC6723C999A']);
export function resolveStatusDelete(row: Row, input: { statusId: string; craftingProgressId: string | null; definition: (id: string) => Row | null }):
  GmsResolution<{ present: 1 }, { status_removed: { status_id: string; entry: null } }> {
  const statusId = upper(input.statusId);
  const entry = statusEntry(row, statusId);
  if (!entry) return { supported: false, refused: false, reason: 'status-missing' };
  const itemId = upper(entry.status_item_id ?? asRow(asRow(entry.status_arr)?.items)?.item_id);
  if (input.craftingProgressId && itemId === upper(input.craftingProgressId)) {
    return { supported: false, refused: true, message: 'This crafting progress marker is managed by the item it tracks and cannot be deleted directly.' };
  }
  // N6e (2026-10-02, R-065 — the legacy delete endpoints are DELETED): a removal tail, a zone anchor and a shapeshift revert are no longer
  // handed back — the commit runs the same removers + the legacy endpoint's own extras (the crafting worker settle, the shapeshift
  // reposition) and rebuilds every row they touched. The page removes the row itself; it builds no C1 claim for them (`rebuild`).
  const done = { supported: true as const, before: { present: 1 as const }, after: { status_removed: { status_id: statusId, entry: null } } };
  if (STATUS_REMOVAL_TAIL_IDS.has(itemId)) return { ...done, rebuild: 'removal-tail' };
  if (Number(scalar(entry.status_aoe_source) ?? 0) === 1) return { ...done, rebuild: 'zone-anchor' };
  const def = input.definition(itemId) ?? asRow(entry.status_arr);
  if (def && String(scalar(asRow(def.items)?.item_type ?? def.item_type) ?? '').toLowerCase() === 'creature') return { ...done, rebuild: 'shapeshift' };
  return done;
}

// ---------------------------------------------------------------------------------------------------------------------
// S9 — GM life actions: the browser checks the held actor; the boundary lane saves its verdict.
export type CreatureLifeAction = 'kill' | 'resurrect' | 'destroy' | 'flee';
export function resolveCreatureLife(row: Row, input: { action: CreatureLifeAction }): GmsResolution<{ dead: 0 | 1 }, { creature_state: { dead: 0 | 1 } }> {
  if (!uuidRe.test(ecId(row))) return { supported: false, refused: false, reason: 'actor-row' };
  const dead: 0 | 1 = Number(ecLean(row, 'encounter_creature_dead')) === 1 ? 1 : 0;
  if (input.action === 'flee' && dead === 1) return { supported: false, refused: true, message: 'A dead creature cannot flee.' };
  const after: 0 | 1 = input.action === 'kill' ? 1 : input.action === 'resurrect' ? 0 : dead;
  return { supported: true, before: { dead }, after: { creature_state: { dead: after } } };
}
/** The browser twin of the saved life state landing on the row (the dead flag; a destroyed row leaves with the fan-out). */
export function applyCreatureState(row: Row, state: unknown): void {
  const st = asRow(state);
  if (!st || !Number.isFinite(Number(st.dead))) return;
  const ec = (row.encounter_creatures ??= {}) as Row;
  ec.encounter_creature_dead = Number(st.dead) === 1 ? 1 : 0;
}
